import React from 'react';
import { DatoCmsInformationCardListItem, DatoCmsLink } from '../../../schema/datocms/blocks';
import { Alignments, Alignment } from '../../../schema/enum/alignments';
import { getButtonClass, getFlexJustification, getTextAlignmentClass } from '../../../utilities';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import HtmlStringComponent from '../../html-string/html-string-component';
import Link from '../../link/link';
import ApiImageComponent from '../api-image/api-image-component';

const InformationCardItemComponent = ({ title, titleAlignment, showTitleAsLink, descriptionHtml,
    imageAlignment, showImageAsLink, links, seeMoreButtonText, seeMoreButtonAlignment, showSeeMoreButton, apiImage
}: DatoCmsInformationCardListItem): React.ReactElement => {
    const isCenterAligned: boolean = imageAlignment === Alignments.CENTER;
    const isRightAligned: boolean = imageAlignment === Alignments.RIGHT;
    const link: DatoCmsLink | undefined = links && links.length > 0 ? links[0] : undefined;

    let imageContent: JSX.Element | undefined = undefined;
    if (apiImage && apiImage[0]) {
        imageContent = (
            <ApiImageComponent className="rounded-3xl" apiImage={apiImage[0]} />
        );
        if (showImageAsLink && link) {
            imageContent = (
                <Link
                    className="flex-shrink-0"
                    internalPageReference={link.internalPageReference}
                    internalPageSectionId={link.internalSectionComponentId}
                    externalPageUrl={link.externalPageUrl}
                    title={link.descriptiveTitle}
                >{imageContent}</Link>);
        }
    }

    let titleContent = (title && <h3 className={`text-center md:${getTextAlignmentClass(titleAlignment)}`}>
        { <HtmlEntityTextComponent text={title} /> }
    </h3>);
    if (showTitleAsLink && link) {
        titleContent = (
            <Link
                internalPageReference={link.internalPageReference}
                internalPageSectionId={link.internalSectionComponentId}
                externalPageUrl={link.externalPageUrl}
                title={link.descriptiveTitle}
            >{titleContent}</Link>);
    }

    let seeMoreButton: JSX.Element | undefined = undefined;
    if (showSeeMoreButton && link) {
        const seeMoreAlignment: Alignment = seeMoreButtonAlignment ? seeMoreButtonAlignment : Alignments.RIGHT;
        seeMoreButton = (
            <div className={`flex justify-center md:${getFlexJustification(seeMoreAlignment)} font-bold uppercase`}><Link
                internalPageReference={link.internalPageReference}
                internalPageSectionId={link.internalSectionComponentId}
                externalPageUrl={link.externalPageUrl}
                title={link.descriptiveTitle}
                className={`${getButtonClass(link.styleOverride)}`}
            >{seeMoreButtonText}</Link></div>);
    }

    // card layout is set for md and larger screens. on mobile sizes, everything needs
    // to be vertically stacked and center aligned
    const contentDirectionClass: string = isCenterAligned ? 'flex-col' : 'flex-row';
    const textOrderClass: string = isRightAligned ? 'order-last md:order-first' : 'order-last';
    let itemSpacingClass: string = isCenterAligned ? 'space-y-4' : 'space-y-4 md:space-x-4 md:space-y-0';
    if (isRightAligned) {
        // the image comes first in the DOM so when changing the display order we need to reverse the spacing
        itemSpacingClass = `${itemSpacingClass} space-x-reverse`;
    }

    const listItem = (
        <div className={`flex flex-col md:${contentDirectionClass} items-left ${itemSpacingClass} info-card`}>
            {imageContent}

            <div className={`${textOrderClass} space-y-4 pb-8 md:pb-0`}>
                { titleContent }

                { descriptionHtml && <HtmlStringComponent htmlString={descriptionHtml} />}

                { seeMoreButton }
            </div>
        </div>
    );

    return listItem;
};

export default InformationCardItemComponent;
